import React, { useState, useEffect } from "react";
import { leaderboardAPI, listAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

const Leaderboard = () => {
  usePageTitle("Leaderboard");

  const [activeTab, setActiveTab] = useState("completed");
  const [completedListsData, setCompletedListsData] = useState(null);
  const [correctAnswersData, setCorrectAnswersData] = useState(null);
  const [fastestTimesData, setFastestTimesData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load completed lists and correct answers leaderboards
  useEffect(() => {
    loadGlobalLeaderboards();
  }, []);

  // Load fastest times when list is selected
  useEffect(() => {
    if (selectedList) {
      loadFastestTimes(selectedList.id);
      setShowDropdown(false);
    }
  }, [selectedList]);

  // Search for lists when user types (debounced)
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const delaySearch = setTimeout(() => {
      searchLists(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const loadGlobalLeaderboards = async () => {
    try {
      setLoading(true);
      const [completedRes, answersRes] = await Promise.all([
        leaderboardAPI.getCompletedListsLeaderboard(),
        leaderboardAPI.getCorrectAnswersLeaderboard(),
      ]);
      setCompletedListsData(completedRes.data);
      setCorrectAnswersData(answersRes.data);
    } catch (err) {
      setError("Failed to load leaderboards");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchLists = async (query) => {
    try {
      setSearching(true);
      const res = await listAPI.searchLists(query, 0, 50);
      setSearchResults(res.data.content || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Failed to search lists", err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const loadFastestTimes = async (listId) => {
    try {
      setLoading(true);
      setShowDropdown(false);
      const res = await leaderboardAPI.getFastestTimesLeaderboard(listId);
      setFastestTimesData(res.data);
      setError(null);
    } catch (err) {
      setError("Failed to load fastest times");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    }
    return `${seconds}s`;
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return "Not yet updated";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleListSelect = (list) => {
    setSelectedList(list);
    setSearchQuery("");
    setShowDropdown(false);
    setSearchResults([]);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && !e.target.closest(".search-container")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const renderLeaderboardTable = (data, valueLabel, formatValue) => {
    if (!data || !data.entries || data.entries.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          No leaderboard data yet. Be the first to appear here!
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 w-20">
                Rank
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">
                Player
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 w-32">
                {valueLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((entry) => (
              <tr
                key={entry.userId}
                className="border-b border-gray-800 hover:bg-dark-hover transition"
              >
                <td className="px-4 py-3 text-left text-gray-400 w-20">
                  {entry.rank <= 3 ? (
                    <span className="text-xl">
                      {entry.rank === 1 && "🥇"}
                      {entry.rank === 2 && "🥈"}
                      {entry.rank === 3 && "🥉"}
                    </span>
                  ) : (
                    `#${entry.rank}`
                  )}
                </td>
                <td className="px-4 py-3 text-left text-white font-medium">
                  {entry.username}
                </td>
                <td className="px-4 py-3 text-right text-blue-400 font-semibold w-32">
                  {formatValue ? formatValue(entry.value) : entry.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading && !completedListsData && !correctAnswersData) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12 text-gray-400">
          Loading leaderboards...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Leaderboard</h1>
        <p className="text-gray-400">
          Compete with other players and climb to the top!
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="text-blue-400 font-semibold text-sm mb-1">
              Leaderboard Updates
            </h3>
            <p className="text-blue-200/90 text-sm leading-relaxed">
              Global leaderboards (Completed Lists & Correct Answers) update
              daily at midnight UTC. Fastest Times update in real-time when you
              complete a list. You can hide yourself from leaderboards in your
              Profile settings.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-700">
        <button
          onClick={() => setActiveTab("completed")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "completed"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Most Completed Lists
        </button>
        <button
          onClick={() => setActiveTab("answers")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "answers"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Most Correct Answers
        </button>
        <button
          onClick={() => setActiveTab("fastest")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "fastest"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Fastest Times
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Completed Lists Leaderboard */}
      {activeTab === "completed" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              Top 100 - Most Completed Lists
            </h2>
            <div className="text-sm text-gray-400">
              Last updated: {formatLastUpdated(completedListsData?.lastUpdated)}
            </div>
          </div>
          {renderLeaderboardTable(completedListsData, "Lists Completed")}
        </div>
      )}

      {/* Correct Answers Leaderboard */}
      {activeTab === "answers" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              Top 100 - Most Correct Answers
            </h2>
            <div className="text-sm text-gray-400">
              Last updated: {formatLastUpdated(correctAnswersData?.lastUpdated)}
            </div>
          </div>
          {renderLeaderboardTable(correctAnswersData, "Correct Answers")}
        </div>
      )}

      {/* Fastest Times Leaderboard */}
      {activeTab === "fastest" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">
              Top 100 - Fastest Completion Times
            </h2>

            {/* Search Bar */}
            <div className="relative search-container">
              <label className="block text-gray-300 mb-2 text-sm">
                Search for a list to view fastest times:
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type at least 2 characters to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() =>
                    searchQuery.length >= 2 && setShowDropdown(true)
                  }
                  className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                {searching && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>

              {/* Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-dark-card border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((list) => (
                    <button
                      key={list.id}
                      onClick={() => handleListSelect(list)}
                      className="w-full text-left px-4 py-2 hover:bg-dark-hover text-white transition"
                    >
                      {list.title}
                    </button>
                  ))}
                </div>
              )}

              {showDropdown &&
                searchQuery.length >= 2 &&
                searchResults.length === 0 &&
                !searching && (
                  <div className="absolute z-10 w-full mt-1 bg-dark-card border border-gray-600 rounded-lg shadow-lg px-4 py-3 text-gray-400">
                    No lists found matching "{searchQuery}"
                  </div>
                )}
            </div>
          </div>

          {selectedList && fastestTimesData && (
            <div>
              <div className="mb-4 p-3 bg-dark-bg rounded-lg border border-gray-700">
                <span className="text-sm text-gray-400">
                  Showing times for:{" "}
                </span>
                <span className="text-white font-semibold">
                  {selectedList.title}
                </span>
              </div>
              <div className="text-sm text-gray-400 mb-4">
                Updates in real-time
              </div>
              {renderLeaderboardTable(fastestTimesData, "Time", formatTime)}
            </div>
          )}

          {!selectedList && (
            <div className="text-center py-12 text-gray-400">
              Search and select a list above to view the fastest completion
              times
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
